import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { LAYOUT_NOTES } from '@/constants/campaign-notes';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign.ts';
import { LogoWithAppName } from '@/pages/Onboarding/components/LogoWithAppName';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  AGE_OPTIONS,
  AGE_OPTIONS_COMPACT,
} from '@/pages/Onboarding/options/Age/AGE_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { Option, OptionList } from './components/Option';
import { GENDER_OPTIONS } from './constants';
import {
  MainImgStyles,
  OptionsContainerStyles,
  ScreenStyles,
  TitleStyles,
} from './Gender.styles';

// Get gender screen image path based on layout notes
const getGenderImagePath = (): string => {
  const layoutNotes = webCampaign.getLayoutNotes();

  if (layoutNotes.includes(LAYOUT_NOTES.GENDER_GIRLS_2)) {
    return `${S3_BUCKET_URL}/layout-own-ai-multigender/picture-2.png`;
  }
  if (layoutNotes.includes(LAYOUT_NOTES.GENDER_GIRLS_4)) {
    return `${S3_BUCKET_URL}/layout-own-ai-multigender/picture-4.png`;
  }
  if (layoutNotes.includes(LAYOUT_NOTES.GENDER_GIRLS_5)) {
    return `${S3_BUCKET_URL}/layout-own-ai-multigender/picture-5.png`;
  }
  if (layoutNotes.includes(LAYOUT_NOTES.GENDER_KELLY)) {
    return `${S3_BUCKET_URL}/layout-own-ai-multigender/picture-6.png`;
  }

  // Default image
  return `${S3_BUCKET_URL}/layout-own-ai-multigender/picture.png`;
};

// Preload Age images for both male and female genders
const preloadAgeImages = () => {
  const layoutNotes = webCampaign.getLayoutNotes();
  const isCompactOnboarding = layoutNotes.includes(
    LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS,
  );

  const ageOptions = isCompactOnboarding ? AGE_OPTIONS_COMPACT : AGE_OPTIONS;

  // Preload male age images
  ageOptions.forEach((ageOption) => {
    const maleImagePath = isCompactOnboarding
      ? `${S3_BUCKET_URL}/layout-own-ai-man/age/compact/sprite-${ageOption.id}.png`
      : `${S3_BUCKET_URL}/layout-own-ai-man/age/sprite-${ageOption.id}.png`;
    preloadImage(maleImagePath);
  });

  // Preload female age images
  ageOptions.forEach((ageOption) => {
    const femaleImagePath = `${S3_BUCKET_URL}/layout-own-ai/age/sprite-${ageOption.id}.png`;
    preloadImage(femaleImagePath);
  });
};

export const ScreenGender: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Gender',
      });
    }

    // Preload Age images for next screen
    preloadAgeImages();
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();
  const gender = form.watch('gender');
  const layoutNotes = webCampaign.getLayoutNotes();

  const handleSelect = (option: OnbOptionType<LayoutDataType['gender']>) => {
    form.setValue('gender', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        gender: option.id,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const showAppLogo = !layoutNotes.includes(
    LAYOUT_NOTES.REMOVE_DIALOGUE_REFERENCES,
  );

  const genderImagePath = getGenderImagePath();

  return (
    <ScreenStyles>
      <IonContent scrollY={false}>
        {showAppLogo && <LogoWithAppName />}

        <MainImgStyles>
          <img src={genderImagePath} alt="Main" />
        </MainImgStyles>

        <OptionsContainerStyles className="options-container">
          <TitleStyles>Who are you looking for?</TitleStyles>
          <OptionList>
            {GENDER_OPTIONS.map((option) => (
              <Option
                title={option.title}
                subtitle="I am looking for"
                isSelected={gender === option.id}
                onClick={() => handleSelect(option)}
                Icon={option.Icon}
                iconPosition={option.iconPosition}
                key={option.id}
              />
            ))}
          </OptionList>
        </OptionsContainerStyles>
      </IonContent>
    </ScreenStyles>
  );
};
